import { useEffect, useState } from "react";
import axios from "../../axios/axios";
import LoadingOverlay from "react-loading-overlay";
import { Button, Col, Form, Row, Modal } from "react-bootstrap";
import { message, Popconfirm } from "antd";
import DataTable from "react-data-table-component";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment/moment";

export default function CorporateGraphics() {
    const { id } = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(false);

    // Add form state
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [link, setLink] = useState("");
    const [graphics, setGraphics] = useState([]);

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editGraphicId, setEditGraphicId] = useState(null);
    const [editImageFile, setEditImageFile] = useState(null);
    const [editImagePreview, setEditImagePreview] = useState(null);
    const [editLink, setEditLink] = useState("");
    const [headingText, setHeadingText] = useState("");
    const [changeHeadingText, setChangeHeadingText] = useState(false);

    // Pagination state
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);

    const fetchGraphicsList = () => {
        setLoading(true);
        axios.get(`/corporate-client/client-graphic-lists/${id}?page=${page}&limit=${perPage}`)
            .then((res) => {
                setLoading(false);
                if (res.data.status) {
                    setHeadingText(res?.data?.headingText || "");
                    setGraphics(res.data.data || []);
                    setTotal(res?.data?.pagination?.total || 0);
                }
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                // Fallback in case endpoint is not exactly this
                if (err?.response?.status === 404) {
                    // Try another common endpoint pattern if needed
                }
            });
    };

    useEffect(() => {
        if (id) {
            fetchGraphicsList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleImageChange = (e, isEdit = false) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                message.error("Only image files are allowed.");
                e.target.value = null;
                if (isEdit) {
                    setEditImageFile(null);
                    setEditImagePreview(null);
                } else {
                    setImageFile(null);
                    setImagePreview(null);
                }
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                message.error("Maximum file size is 5MB.");
                e.target.value = null;
                if (isEdit) {
                    setEditImageFile(null);
                    setEditImagePreview(null);
                } else {
                    setImageFile(null);
                    setImagePreview(null);
                }
                return;
            }
            if (isEdit) {
                setEditImageFile(file);
                setEditImagePreview(URL.createObjectURL(file));
            } else {
                setImageFile(file);
                setImagePreview(URL.createObjectURL(file));
            }
        } else {
            if (isEdit) {
                setEditImageFile(null);
                // Keep the existing preview if user cancels selection
            } else {
                setImageFile(null);
                setImagePreview(null);
            }
        }
    };

    const handleAddGraphic = async (e) => {
        e.preventDefault();

        if (!imageFile) {
            message.error('Please select an image file to upload.');
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('image', imageFile);
            if (link) formData.append('link', link);
            formData.append('headingText', headingText);

            const res = await axios.post(`/corporate-client/save-client-graphic/${id}`, formData);
            setLoading(false);

            if (res.data.status) {
                message.success(res.data.message || 'Graphic uploaded successfully.');
                fetchGraphicsList();
                setImageFile(null);
                setImagePreview(null);
                setLink("");
                e.target.reset();
            } else {
                message.error(res.data.message || 'Failed to upload graphic.');
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
            message.error(err?.response?.data?.message || err?.message || 'Unable to upload graphic.');
        }
    };

    const openEditModal = (graphic) => {
        setEditGraphicId(graphic._id);
        setEditLink(graphic.link || "");
        setEditImageFile(null); // Keep null unless user selects a new image
        setEditImagePreview(graphic.image || graphic.url);
        setChangeHeadingText(false);
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditGraphicId(null);
        setEditLink("");
        setEditImageFile(null);
        setChangeHeadingText(false);
        setEditImagePreview(null);
    };

    const handleUpdateGraphic = async (e) => {
        e.preventDefault();

        setLoading(true);

        try {
            const formData = new FormData();
            if (editImageFile) {
                formData.append('image', editImageFile);
            }
            formData.append('link', editLink);
            formData.append('clientId', id);
            formData.append('headingText', headingText);

            const res = await axios.patch(`/corporate-client/update-client-graphic/${editGraphicId}`, formData);
            setLoading(false);

            if (res.data.status) {
                message.success(res.data.message || 'Graphic updated successfully.');
                fetchGraphicsList();
                closeEditModal();
            } else {
                message.error(res.data.message || 'Failed to update graphic.');
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
            message.error(err?.response?.data?.message || err?.message || 'Unable to update graphic.');
        }
    };

    const handleDeleteGraphic = async (graphicId) => {
        setLoading(true);
        try {
            const res = await axios.delete(`/corporate-client/delete-client-graphic/${graphicId}`);
            setLoading(false);
            if (res.data.status) {
                message.success(res.data.message || 'Graphic deleted successfully.');
                fetchGraphicsList();
            } else {
                message.error(res.data.message || 'Failed to delete graphic.');
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
            message.error(err?.response?.data?.message || err?.message || 'Unable to delete graphic.');
        }
    };

    const graphicsColumns = [
        {
            name: "Image",
            selector: (row) => row.image || row.url,
            cell: (row) => (
                <img
                    src={row.image || row.url}
                    alt="graphic"
                    style={{ width: '80px', height: 'auto', borderRadius: '4px', margin: '5px 0' }}
                />
            )
        },
        {
            name: "Link",
            selector: (row) => row.link,
            cell: (row) => (
                row.link ? <a href={row.link} target="_blank" rel="noreferrer">{row.link}</a> : "N/A"
            )
        },
        {
            name: "Uploaded At",
            selector: (row) => moment(row.createdAt).format("MM-DD-YYYY HH:mm A"),
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex gap-3">
                    <Button variant="info" size="sm" onClick={() => openEditModal(row)}>
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete Graphic"
                        description="Are you sure you want to delete this graphic?"
                        onConfirm={() => handleDeleteGraphic(row._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button variant="danger" size="sm" className="ml-3">
                            Delete
                        </Button>
                    </Popconfirm>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "150px",
        }
    ];

    const handlePageChange = (page) => {
        setPage(page);
    };

    const handleRowsPerPageChange = (newPerPage, page) => {
        setPage(page);
        setPerPage(newPerPage);
    };

    const handleUpdateHeading = async () => {
        setLoading(true)
        try {
            const res = await axios.post(`/corporate-client/update-graphic-heading`, {
                headingText: headingText,
                clientId: id
            })
            setLoading(false)
            if (res.data.status) {
                message.success(res.data.message || 'Heading updated successfully.');
            } else {
                message.error(res.data.message || 'Failed to update heading.');
            }
        } catch (err) {
            setLoading(false)
            console.error(err)
            message.error(err?.response?.data?.message || err?.message || 'Unable to update heading.')
        }
    }

    useEffect(() => {
        fetchGraphicsList();
    }, [page, perPage]);

    return (
        <LoadingOverlay active={loading} spinner text="loading...">
            <div className="right_cont">
                <div className="right_banner_banner_cnt">
                    <div className="travel_deals_admin">
                        <div className="cert_det_inner">
                            <h2>Corporate Client Graphics {graphics[0]?.clientDetails?.[0]?.name ? `of ${graphics[0].clientDetails[0].name}` : ''}</h2>
                            <Button variant="secondary" onClick={() => history.goBack()} className="mb-3">
                                Back
                            </Button>
                        </div>
                        <div className="py-2">
                            <h3 className="text-center mb-4 fw-bold text-primary">Change Graphic Heading</h3>
                            <Form>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Heading Text</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter Heading Text"
                                                value={headingText}
                                                onChange={(e) => setHeadingText(e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} className="mt-4">
                                        <div className="d-flex justify-content-end mb-4">
                                            <Button variant="primary" type="button" onClick={handleUpdateHeading} className="px-5 py-2 fw-bold">
                                                Update Heading Text
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>

                            </Form>
                        </div>
                        <div className="py-2">
                            <h3 className="text-center mb-4 fw-bold text-primary">Add New Graphic</h3>

                            <Form onSubmit={handleAddGraphic}>
                                <Row>
                                    {/* <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Heading Text</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter Heading Text"
                                                value={headingText}
                                                onChange={(e) => setHeadingText(e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col> */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Upload Image *</Form.Label>
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleImageChange(e, false)}
                                                required
                                            />
                                            {imagePreview && (
                                                <div className="mt-3">
                                                    <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '5px' }} />
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Link (Optional)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter URL"
                                                value={link}
                                                onChange={(e) => setLink(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end mb-4">
                                    <Button variant="primary" type="submit" className="px-5 py-2 fw-bold">
                                        Add Graphic
                                    </Button>
                                </div>
                            </Form>

                            <div className="invoice-file-list mt-5">
                                <h5 className="mb-3">Graphics List</h5>
                                <DataTable
                                    columns={graphicsColumns}
                                    data={graphics}
                                    pagination
                                    paginationServer
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handleRowsPerPageChange}
                                    paginationPerPage={perPage}
                                    paginationRowsPerPageOptions={[5, 10, 25, 50]}
                                    paginationTotalRows={total}
                                    striped
                                    highlightOnHover
                                    noHeader
                                    responsive
                                    noDataComponent="No graphics uploaded yet."
                                    customStyles={{
                                        headCells: {
                                            style: {
                                                backgroundColor: '#f8f9fa',
                                                color: '#495057',
                                                fontWeight: 'bold',
                                                fontSize: '15px',
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <Modal show={showEditModal} onHide={closeEditModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Graphic</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form id="editGraphicForm" onSubmit={handleUpdateGraphic}>
                        <Form.Group className="mb-3">
                            <Form.Label>Upload New Image (Leave blank to keep existing)</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageChange(e, true)}
                            />
                            {editImagePreview && (
                                <div className="mt-3 text-center">
                                    <img src={editImagePreview} alt="Edit Preview" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '5px' }} />
                                </div>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Link (Optional)</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter URL"
                                value={editLink}
                                onChange={(e) => setEditLink(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeEditModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" form="editGraphicForm">
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </LoadingOverlay>
    );
}

